import type { StudioAuthorityDomain } from "@/shell/ide-v2/studio-authority-workspaces";
import type {
  StudioCapabilityExecution,
  StudioPendingCapability,
} from "./studio-capability-gateway";
import type { StudioTaskSnapshot } from "./studio-task-orchestrator";

export interface StudioArtifactImpact {
  domain: StudioAuthorityDomain;
  changeCount: number;
  approvalPending: boolean;
}

export interface StudioCommandDeckSnapshot {
  goal: string | null;
  affectedArtifacts: StudioArtifactImpact[];
  changeCount: number;
  approvalRequired: boolean;
  impactAvailable: boolean;
}

// "as-is" (G1 alternative A) is a read-only view of the captured original —
// no command/tool ever writes into it, so no evidence path or tool name can
// legitimately classify as an "as-is" impact. Excluded from this closed
// classification set on purpose (not merely unhandled): a real regex here
// would misclassify unrelated evidence rather than reflect any actual
// as-is-domain impact.
type ImpactableDomain = Exclude<StudioAuthorityDomain, "as-is">;

const DOMAIN_ORDER: readonly ImpactableDomain[] = [
  "primary",
  "design",
  "guide",
  "insights",
];

const DOMAIN_PATTERNS: Record<ImpactableDomain, RegExp> = {
  primary:
    /(?:^|[/._-])(apps?|games?|pages?|blocks?|elements?|actions?|flows?|automations?|data|documents?|projects?|releases?|publish|deploy|previews?)(?:$|[/._-])/i,
  design:
    /(?:^|[/._-])(design|figma|openpencil|tokens?|themes?|styles?|visual)(?:$|[/._-])/i,
  guide:
    /(?:^|[/._-])(guides?|guidelines?|manuals?|documents?|cogoDocumentWorkspace|documentWorkspace)(?:$|[/._-])/i,
  insights:
    /(?:^|[/._-])(insights?|evidence|metrics?|analytics|validation|readback|provenance|knowledge)(?:$|[/._-])/i,
};

function classifyEvidence(value: string): StudioAuthorityDomain[] {
  const [root = ""] = value.split(/[/._-]+/).filter(Boolean);
  const scopedDomain = DOMAIN_ORDER.find(
    (domain) =>
      domain !== "primary" && DOMAIN_PATTERNS[domain].test(`/${root}`),
  );
  if (scopedDomain) return [scopedDomain];

  return DOMAIN_ORDER.filter((domain) => DOMAIN_PATTERNS[domain].test(value));
}

function countImpactDomains(input: {
  paths: string[];
  fallbackDomain: StudioAuthorityDomain | null;
}): Map<StudioAuthorityDomain, Set<string>> {
  const counts = new Map<StudioAuthorityDomain, Set<string>>();
  const add = (domain: StudioAuthorityDomain, evidence: string) => {
    const existing = counts.get(domain) ?? new Set<string>();
    existing.add(evidence);
    counts.set(domain, existing);
  };

  for (const path of input.paths) {
    const domains = classifyEvidence(path);
    if (domains.length === 0) {
      // Every exact path remains visible. The dynamic primary artifact is the
      // safe owning surface when the path vocabulary has no narrower match.
      add(input.fallbackDomain ?? "primary", path);
      continue;
    }
    for (const domain of domains) add(domain, path);
  }

  return counts;
}

function mergeImpactCounts(
  target: Map<StudioAuthorityDomain, Set<string>>,
  source: Map<StudioAuthorityDomain, Set<string>>,
): void {
  for (const [domain, evidence] of source) {
    const existing = target.get(domain) ?? new Set<string>();
    for (const item of evidence) existing.add(item);
    target.set(domain, existing);
  }
}

function pendingImpactCounts(
  pending: StudioPendingCapability,
  fallbackDomain: StudioAuthorityDomain | null = null,
): Map<StudioAuthorityDomain, Set<string>> {
  const paths = Array.from(
    new Set(
      [
        ...pending.changedPaths,
        ...pending.impact.changes.map((change) => change.path),
      ].filter((path) => path.trim().length > 0),
    ),
  );
  const counts = countImpactDomains({
    paths,
    fallbackDomain,
  });
  const surfaceDomains = {
    app: "primary",
    design: "design",
    guide: "guide",
    insights: "insights",
    release: "primary",
  } as const;
  for (const surface of pending.impact.affectedSurfaces ?? []) {
    const domain = surfaceDomains[surface];
    if (!counts.has(domain)) counts.set(domain, new Set([`surface:${surface}`]));
  }
  return counts;
}

function executionImpactCounts(
  execution: StudioCapabilityExecution,
  fallbackDomain: StudioAuthorityDomain | null = null,
): Map<StudioAuthorityDomain, Set<string>> {
  return countImpactDomains({
    paths: (execution.changedPaths ?? []).filter(
      (path) => path.trim().length > 0,
    ),
    fallbackDomain,
  });
}

export function summarizePendingStudioArtifactImpacts(
  pending: StudioPendingCapability[],
): StudioArtifactImpact[] {
  const counts = new Map<StudioAuthorityDomain, Set<string>>();
  for (const item of pending)
    mergeImpactCounts(counts, pendingImpactCounts(item));

  return DOMAIN_ORDER.flatMap((domain) => {
    const evidence = counts.get(domain);
    return evidence
      ? [{ domain, changeCount: evidence.size, approvalPending: true }]
      : [];
  });
}

export function buildStudioCommandDeckSnapshot(input: {
  task: StudioTaskSnapshot;
  projectIntent?: string | null;
}): StudioCommandDeckSnapshot {
  const pending = input.task.pending;
  const execution = input.task.execution;
  const counts = pending
    ? pendingImpactCounts(pending, input.task.authorityDomain)
    : execution
      ? executionImpactCounts(execution, input.task.authorityDomain)
      : new Map<StudioAuthorityDomain, Set<string>>();
  const affectedArtifacts = DOMAIN_ORDER.flatMap((domain) => {
    const evidence = counts.get(domain);
    return evidence
      ? [
          {
            domain,
            changeCount: evidence.size,
            approvalPending: Boolean(pending),
          },
        ]
      : [];
  });
  const changeCount = pending
    ? new Set(
        [
          ...pending.changedPaths,
          ...pending.impact.changes.map((change) => change.path),
        ].filter((path) => path.trim().length > 0),
      ).size
    : execution
      ? new Set(
          (execution.changedPaths ?? []).filter(
            (path) => path.trim().length > 0,
          ),
        ).size
      : 0;

  return {
    goal: input.projectIntent?.trim() || null,
    affectedArtifacts,
    changeCount,
    approvalRequired: Boolean(pending) && changeCount > 0,
    impactAvailable: changeCount > 0,
  };
}
